import { createElement } from 'react';
import { createRoute, type AnyRoute } from '@tanstack/react-router';
import { CAMINHO_DETALHE, CAMINHO_LISTA } from '@/features/clientes/navegacao';
import { ClienteDetalhePage } from '@/features/clientes/ClienteDetalhePage';
import { ClientesPage } from '@/features/clientes/ClientesPage';

/**
 * Rotas das telas de cliente.
 *
 * Exportadas como **fábrica**, e não como objetos prontos, pelo mesmo motivo
 * de `features/tickets/routes.ts` e `features/admin/routes.ts`: `createRoute`
 * exige a rota pai, e o pai (`appRoute`, com o `AppShell` e a guarda de
 * sessão) mora em `app/router.tsx`. Receber o pai por parâmetro deixa a
 * integração ser uma linha lá, sem que esta feature importe de dentro de
 * `app/` — o que fecharia um ciclo entre o roteador e a tela que ele monta.
 *
 * Duas rotas irmãs, e não uma raiz com filhas como na administração: lista e
 * ficha não compartilham casca. A administração tem uma barra de abas que
 * precisa envolver as cinco telas; aqui a ficha é uma tela inteira, com
 * cabeçalho próprio e um caminho de volta.
 *
 * Integração esperada em `app/router.tsx`:
 *
 * ```ts
 * const { lista: clientesRoute, detalhe: clienteRoute } = criarRotasDeClientes(appRoute);
 * appRoute.addChildren([indexRoute, clientesRoute, clienteRoute]);
 * ```
 */
export function criarRotasDeClientes<TRotaPai extends AnyRoute>(rotaPai: TRotaPai) {
  const lista = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_LISTA,
    component: ClientesPage,
  });

  const detalhe = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_DETALHE,
    // `createElement` em vez de JSX para este arquivo continuar sendo `.ts`, e
    // a conversão do parâmetro porque o roteador só infere `{ clienteId }`
    // depois que a árvore estiver registrada em `app/router.tsx`. Mesma
    // mecânica de `features/tickets/routes.ts`.
    component: () => {
      const { clienteId } = detalhe.useParams() as { clienteId: string };
      return createElement(ClienteDetalhePage, { clienteId });
    },
  });

  return { lista, detalhe };
}

export { CAMINHO_DETALHE, CAMINHO_LISTA };
